import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { DOMAIN_API_HOST } from "../configs/main";
import {
  isModerationNotifier,
  MODERATION_POLL_INTERVAL_MS,
  MODERATION_KNOWN_IDS_KEY,
} from "../constants/moderation";
import { enqueueSnackbar as enqueueSnackbarAction } from "../redux/notifications/actions";

export const ModerationNotificationsContext = createContext({
  count: 0,
  enabled: false,
});

const loadKnownIds = () => {
  try {
    const raw = sessionStorage.getItem(MODERATION_KNOWN_IDS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed) : null;
  } catch {
    return null;
  }
};

const saveKnownIds = (ids) => {
  try {
    sessionStorage.setItem(MODERATION_KNOWN_IDS_KEY, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
};

const getAuthToken = () =>
  localStorage.getItem("admin_token") || localStorage.getItem("token");

function useModerationNotifications(email) {
  const dispatch = useDispatch();
  const enabled = isModerationNotifier(email);
  const knownIdsRef = useRef(loadKnownIds());
  const [count, setCount] = useState(0);

  const notifyNewCampaigns = useCallback(
    (campaigns) => {
      const currentIds = new Set(campaigns.map((c) => String(c.id)));

      if (knownIdsRef.current !== null) {
        campaigns.forEach((campaign) => {
          const id = String(campaign.id);
          if (!knownIdsRef.current.has(id)) {
            dispatch(
              enqueueSnackbarAction({
                message: `Новое объявление на модерации\n${campaign.campaign_creator_email}`,
                options: {
                  variant: "info",
                  anchorOrigin: { vertical: "top", horizontal: "right" },
                  autoHideDuration: 10000,
                  preventDuplicate: true,
                },
              })
            );
          }
        });
      }

      knownIdsRef.current = currentIds;
      saveKnownIds(currentIds);
    },
    [dispatch]
  );

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return undefined;
    }

    let cancelled = false;

    const fetchSummary = async () => {
      const token = getAuthToken();
      if (!token) return;

      try {
        const res = await fetch(
          `${DOMAIN_API_HOST}/api/v1/admin/campaigns/moderation-summary`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (cancelled) return;

        const campaigns = Array.isArray(data?.campaigns) ? data.campaigns : [];
        setCount(typeof data?.count === "number" ? data.count : campaigns.length);
        notifyNewCampaigns(campaigns);
      } catch {
        // ignore polling errors
      }
    };

    fetchSummary();
    const intervalId = window.setInterval(fetchSummary, MODERATION_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [enabled, notifyNewCampaigns]);

  return { count, enabled };
}

export function ModerationNotificationsProvider({ email, children }) {
  const value = useModerationNotifications(email);
  return (
    <ModerationNotificationsContext.Provider value={value}>
      {children}
    </ModerationNotificationsContext.Provider>
  );
}

export function useModerationNotificationsState() {
  return useContext(ModerationNotificationsContext);
}

export default useModerationNotifications;
